import {
  DEFAULT_EVENT_DISCOVERY_RADIUS_KM,
  normalizeOptionalEventDiscoveryRadiusKm,
} from "./eventDiscoveryRadius";
import { normalizeSelectedEventCity } from "./eventCitySelection";
import { normalizeProfileCountry } from "../constants/profileLocations";

const EVENT_LOCATION_FILTER_STORAGE_PREFIX = "vg-events-location-filter-v4";
const PREVIOUS_EVENT_LOCATION_FILTER_STORAGE_PREFIX = "vg-events-location-filter-v3";
const LEGACY_EVENT_LOCATION_FILTER_STORAGE_PREFIX = "vg-events-location-filter-v2";

const normalizeMarketRegion = (marketRegion) =>
  String(marketRegion || "").trim().toUpperCase() === "RU" ? "RU" : "EU";

export const normalizeMunicipalityId = (value) => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const countrySuffix = (marketRegion, country) => {
  const code = normalizeProfileCountry(country);
  return normalizeMarketRegion(marketRegion) === "EU" && code && code !== "SK" && code !== "RU"
    ? `:${code}`
    : "";
};

export const getEventLocationFilterStorageKey = (marketRegion, country = "") => {
  const region = normalizeMarketRegion(marketRegion);
  return `${EVENT_LOCATION_FILTER_STORAGE_PREFIX}:${region}${countrySuffix(region, country)}`;
};

const legacyStorageKey = (marketRegion, country) => {
  const region = normalizeMarketRegion(marketRegion);
  return `${LEGACY_EVENT_LOCATION_FILTER_STORAGE_PREFIX}:${region}${countrySuffix(region, country)}`;
};

const previousStorageKey = (marketRegion, country) => {
  const region = normalizeMarketRegion(marketRegion);
  return `${PREVIOUS_EVENT_LOCATION_FILTER_STORAGE_PREFIX}:${region}${countrySuffix(region, country)}`;
};

export const readEventLocationFilter = (marketRegion, country = "") => {
  if (typeof window === "undefined") return null;
  try {
    const key = getEventLocationFilterStorageKey(marketRegion, country);
    const previousKey = previousStorageKey(marketRegion, country);
    const legacyKey = legacyStorageKey(marketRegion, country);
    const current = window.localStorage.getItem(key);
    const previous = window.localStorage.getItem(previousKey);
    const raw = current || previous || window.localStorage.getItem(legacyKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const city = normalizeSelectedEventCity(country || marketRegion, parsed?.city);
    if (!city) return null;
    const municipalityId = normalizeMunicipalityId(parsed?.municipalityId);

    // v3 saved +20 automatically for every member. Its default cannot be
    // distinguished from a manual +20 choice, so reset that one value once.
    const radiusKm = !current && previous && Number(parsed.radiusKm) === 20
      ? null
      : normalizeOptionalEventDiscoveryRadiusKm(parsed.radiusKm);

    const selected = {
      city,
      radiusKm,
      ...(municipalityId ? { municipalityId } : {}),
    };
    // Preserve the selected city and any distinct custom radius across the
    // switch to the unbounded city-feed default.
    if (!current) {
      window.localStorage.setItem(key, JSON.stringify(selected));
      window.localStorage.removeItem(previousKey);
      window.localStorage.removeItem(legacyKey);
    }
    return selected;
  } catch {
    return null;
  }
};

export const writeEventLocationFilter = (
  marketRegion,
  city,
  radiusKm = DEFAULT_EVENT_DISCOVERY_RADIUS_KM,
  country = "",
  municipalityId = null
) => {
  if (typeof window === "undefined") return;
  const key = getEventLocationFilterStorageKey(marketRegion, country);
  const previousKey = previousStorageKey(marketRegion, country);
  const legacyKey = legacyStorageKey(marketRegion, country);
  const normalizedCity = normalizeSelectedEventCity(country || marketRegion, city);
  const normalizedMunicipalityId = normalizeMunicipalityId(municipalityId);
  try {
    window.localStorage.removeItem(legacyKey);
    window.localStorage.removeItem(previousKey);
    if (!normalizedCity) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(
      key,
      JSON.stringify({
        city: normalizedCity,
        radiusKm: normalizeOptionalEventDiscoveryRadiusKm(radiusKm),
        ...(normalizedMunicipalityId ? { municipalityId: normalizedMunicipalityId } : {}),
      })
    );
  } catch {
    // Storage can be unavailable in private/restricted web views.
  }
};
